import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from '@/i18n';
import { usePastedFields } from '@/contexts/PastedFieldsContext';
import { HotelFormDataWithTranslations } from '@/i18n/translation-type-utils';
import type { HotelPensionType } from '@/types/benefits/hotel/HotelResponse';
import {
  buildEmptyHotelSupplement,
  buildEmptyHotelSupplementPricing,
} from '@/utils/benefits/hotel/buildEmptyHotelSupplementCard';
import {
  classifyPastedTariffs,
  findPastedOverlaps,
  matchPastedTariffs,
} from '@/utils/benefits/hotel/excelPaste/multiTariffPaste';
import {
  groupBySupplement,
  matchPastedSupplements,
  parseSupplementTariffsBlock,
} from '@/utils/benefits/hotel/excelPaste/parseSupplementTariffBlock';
import type { ChildBandsCount } from '@/utils/benefits/hotel/excelPaste/types';
import ExcelPastePanel from './ExcelPastePanel';
import { buildTariffNotes, ChildAges } from './pasteFormHelpers';
import {
  buildSupplementRecap,
  getPastedSupplementFieldNames,
  isBlankSupplement,
  isBlankSupplementTariff,
  mergePastedSupplementTariff,
  SupplementCard,
  SupplementTariff,
} from './supplementPasteHelpers';
import { waitForEffects } from './updateCategoryRoomOptions';
import { useExcelPasteFormatters } from './useExcelPasteFormatters';

interface SupplementTariffsPasteProps {
  childBandsCount: ChildBandsCount;
  childAges: ChildAges;
  /** Pension types the supplement cards offer as checkboxes. */
  offeredPensionTypes: HotelPensionType[];
  /** Replaces every supplement (the field array's `replace`). */
  replaceSupplements: (supplements: SupplementCard[]) => void;
}

/**
 * Paste of every festive supplement tariff at once: the 13×3 blocks side by
 * side, optionally under a line naming their supplement. Blocks are grouped
 * by supplement (that name, or the tariff labels without their final year):
 * each group updates the matching supplement, fills a blank one or adds one,
 * then its tariffs are matched by label within the supplement.
 */
const SupplementTariffsPaste = ({
  childBandsCount,
  childAges,
  offeredPensionTypes,
  replaceSupplements,
}: SupplementTariffsPasteProps) => {
  const { t } = useTranslation();
  const { getValues } = useFormContext<HotelFormDataWithTranslations>();
  const { markPasted } = usePastedFields();
  const { formatIssue, formatPensionCodes } =
    useExcelPasteFormatters(childAges);

  const tariffName = useCallback(
    (name: string | null | undefined, index: number) =>
      name || t('tariffs.tarif_', { index: index + 1 }),
    [t],
  );
  const quote = useCallback(
    (name: string) =>
      t('hotels.pasteFromExcel.quoted', {
        name,
        interpolation: { escapeValue: false },
      }),
    [t],
  );

  const parse = useCallback(
    (text: string) => {
      const supplements = (getValues('hotelSupplements') ??
        []) as SupplementCard[];
      const result = parseSupplementTariffsBlock(text, {
        childBandsCount,
        offeredPensionTypes,
        basePensionType: getValues('pricingPensionType')?.value as
          | string
          | undefined,
      });
      if (!result.value) return { result, entries: null, nextSupplements: [] };

      const supplementPlan = matchPastedSupplements(
        groupBySupplement(result.value),
        supplements.map((supplement) => ({
          name: supplement.name,
          tariffNames: (supplement.pricingList ?? []).map(({ name }) => name),
          isBlank: isBlankSupplement(supplement),
        })),
      );
      const nextSupplements = [
        ...supplements,
        ...Array.from(
          { length: supplementPlan.newCount },
          buildEmptyHotelSupplement,
        ),
      ];
      const entries = supplementPlan.targets.map(
        ({ supplementIndex, group }) => {
          const current = supplements[supplementIndex] as
            | SupplementCard
            | undefined;
          // A name read from the tariff labels does not replace the name a
          // supplement already has.
          const supplementName =
            group.isNameDerived && current?.name?.trim()
              ? current.name
              : group.supplementName;
          const tariffs = (nextSupplements[supplementIndex].pricingList ??
            []) as SupplementTariff[];
          const existingTariffs = tariffs.map((tariff) => ({
            name: tariff.name,
            isBlank: isBlankSupplementTariff(tariff),
          }));
          return {
            supplementIndex,
            supplementName,
            group,
            current,
            isAdded: !current || isBlankSupplement(current),
            tariffs,
            existingTariffs,
            tariffPlan: matchPastedTariffs(group.tariffs, existingTariffs),
          };
        },
      );
      return { result, entries, nextSupplements };
    },
    [childBandsCount, getValues, offeredPensionTypes],
  );

  const analyze = useCallback(
    (text: string) => {
      const { result, entries } = parse(text);
      if (!result.value || !entries) {
        return {
          errors: result.errors.map(formatIssue),
          warnings: [],
          recap: [],
        };
      }

      const addedSupplements = entries
        .filter(({ isAdded }) => isAdded)
        .map(({ supplementName }) => quote(supplementName));

      return {
        errors: [],
        warnings: entries.flatMap(({ supplementName, tariffs, tariffPlan }) => {
          const targetIndexes = new Set(
            tariffPlan.targets.map(({ tariffIndex }) => tariffIndex),
          );
          return findPastedOverlaps(
            tariffPlan.targets.map(({ pasted }) => pasted),
            tariffs
              .map((tariff, index) => ({
                name: tariffName(tariff.name, index),
                periods: tariff.periods ?? [],
              }))
              .filter((_, index) => !targetIndexes.has(index)),
          ).map((issue) => `${quote(supplementName)} : ${formatIssue(issue)}`);
        }),
        notes: [
          ...(addedSupplements.length > 0
            ? [
                t('hotels.pasteFromExcel.notes.addedSupplements', {
                  supplements: addedSupplements.join(', '),
                  interpolation: { escapeValue: false },
                }),
              ]
            : []),
          ...entries.flatMap(
            ({ supplementName, existingTariffs, tariffPlan }) =>
              buildTariffNotes(
                classifyPastedTariffs(
                  tariffPlan.targets,
                  existingTariffs,
                  (index) => tariffName(undefined, index),
                ),
                t,
              ).map((note) => `${quote(supplementName)} : ${note}`),
          ),
        ],
        recap: entries.flatMap(
          ({ supplementIndex, supplementName, current, tariffs, tariffPlan }) =>
            tariffPlan.targets.flatMap(({ tariffIndex, pasted }, position) =>
              buildSupplementRecap(
                {
                  supplementName: current?.name,
                  pensionTypes: (current?.pensionTypes ??
                    []) as HotelPensionType[],
                  tariff: tariffs[tariffIndex],
                },
                { ...pasted, supplementName },
                { childBandsCount, childAges, formatPensionCodes },
                t,
              )
                // The supplement's own rows once, with its first tariff.
                .filter(
                  ({ key }) =>
                    position === 0 ||
                    (key !== 'supplementName' && key !== 'pensionTypes'),
                )
                .map((row) => ({
                  ...row,
                  key: `${supplementIndex}-${tariffIndex}-${row.key}`,
                  label:
                    row.key === 'supplementName' || row.key === 'pensionTypes'
                      ? `${supplementName} · ${row.label}`
                      : `${supplementName} · ${pasted.name} · ${row.label}`,
                })),
            ),
        ),
      };
    },
    [
      childAges,
      childBandsCount,
      formatIssue,
      formatPensionCodes,
      parse,
      quote,
      t,
      tariffName,
    ],
  );

  const apply = useCallback(
    async (text: string) => {
      const { entries, nextSupplements } = parse(text);
      if (!entries) return;

      const supplements = [...nextSupplements];
      entries.forEach(
        ({ supplementIndex, supplementName, group, tariffs, tariffPlan }) => {
          const pricingList = [
            ...tariffs,
            ...Array.from(
              { length: tariffPlan.newTariffCount },
              buildEmptyHotelSupplementPricing,
            ),
          ] as SupplementTariff[];
          tariffPlan.targets.forEach(({ tariffIndex, pasted }) => {
            pricingList[tariffIndex] = mergePastedSupplementTariff(
              pricingList[tariffIndex],
              pasted,
            );
          });
          supplements[supplementIndex] = {
            ...supplements[supplementIndex],
            name: supplementName,
            pensionTypes: group.tariffs[0].pensionTypes,
            pricingList,
          } as SupplementCard;
        },
      );
      replaceSupplements(supplements);
      await waitForEffects();

      markPasted(
        entries.flatMap(({ supplementIndex, tariffPlan }) =>
          tariffPlan.targets.flatMap(({ tariffIndex, pasted }) =>
            getPastedSupplementFieldNames(
              `hotelSupplements.${supplementIndex}`,
              tariffIndex,
              pasted,
            ),
          ),
        ),
      );
    },
    [markPasted, parse, replaceSupplements],
  );

  return (
    <ExcelPastePanel
      shape="supplementTariffs"
      title={t('hotels.pasteFromExcel.supplementTariffsButton')}
      analyze={analyze}
      apply={apply}
    />
  );
};

export default SupplementTariffsPaste;
